import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiService } from '../common/api.service';
import { map, of, EMPTY, catchError } from 'rxjs';
import { ACTION_LOG, API_ENDPOINT, CONFIG_LOG, LOCAL_STORAGE_KEY, LOG_TYPE, SAUCER_LOG_ACTION, SESSION_STORAGE_KEY } from '@core/configs';
import { Mode } from '@core/constants/enum.common';
import { LocalStorageService, SessionStorageService } from '../common';
import { SaucerLogModel, SaucerLogQuickConfigModel } from '@core/models/models';
import { FirebaseDeviceInfoService } from '@core/services/common/firebase/firebase-device-info.service';
import { NativeDiagnosticLogService } from '@core/services/diagnostic-log/native-diagnostic-log.service';

export { SAUCER_LOG_ACTION };

@Injectable({ providedIn: 'root' })
export class SaucerLogService extends ApiService {
  private timeoutId: any = null;
  private logs: any[] = [];

  private clientIp: string | any = null;
  private failedIpString: string = 'IP取得失敗';

  constructor(
    http: HttpClient,
    nativeDiagnosticLogService: NativeDiagnosticLogService,
    private localStorageService: LocalStorageService,
    private sessionStorageService: SessionStorageService,
    private firebaseDeviceInfoService: FirebaseDeviceInfoService,
  ) {
    super(http, nativeDiagnosticLogService);
    window.addEventListener('beforeunload', this.saveLogsToLocalStorage.bind(this));
  }

  async writeLog(params: SaucerLogModel, config?: SaucerLogQuickConfigModel | null) {
    (Object.keys(config?.action || []) as (keyof SaucerLogModel)[]).forEach(key => {
      if (!params[key] || params[key] === '') {
        params[key] = config?.action[key];
      }
    });
    params = await this.formatSaucerLogParams(params);
    this.logs.push(params);
    if (CONFIG_LOG.IS_DEBUG && CONFIG_LOG.IS_SHOW_LOCAL_LOG) {
      console.log("===== SAUCER_LOG_RAM =====");
      console.log({LOG_RAM_DATA: this.logs});
      console.log("==========================");
    }
    this.setTimeoutToSendLogs();
  }

  checkClientIpToSendLog(params: SaucerLogModel, config?: SaucerLogQuickConfigModel | null): void {
    if (this.clientIp && this.clientIp !== this.failedIpString) {
      this.writeLog(params, config);
    } else {
      this.getClientIp().subscribe({
        next: (clientIp) => {
          this.setClientIp(clientIp);
        },
        complete: () => {
          this.writeLog(params, config);
        }
      });
    }
  }

  async action(params: SaucerLogModel, config?: SaucerLogQuickConfigModel | null) : Promise<void> {
    params.logType = LOG_TYPE.LOG_ACTION;
    await this.checkClientIpToSendLog(params, config);
  }

  error(params: SaucerLogModel, config?: SaucerLogQuickConfigModel | null): void {
    params.logType = LOG_TYPE.LOG_ERROR;
    this.checkClientIpToSendLog(params, config);
  }

  system(params: SaucerLogModel, config?: SaucerLogQuickConfigModel | null): void {
    params.logType = LOG_TYPE.LOG_SYSTEM;
    this.checkClientIpToSendLog(params, config);
  }

  async formatSaucerLogParams(params: SaucerLogModel) {
    const officeSelected = this.sessionStorageService.getSessionData(SESSION_STORAGE_KEY.officeSelected);
    const staffInfo = this.sessionStorageService.getSessionData(SESSION_STORAGE_KEY.staffInfo);
    const loginSession = this.sessionStorageService.getSessionData(SESSION_STORAGE_KEY.loginSession);

    const organizationId = officeSelected?.organizationId || '';
    const companyId = officeSelected?.companyId || '';
    const companyName = officeSelected?.corpNm || '';
    const officeName = officeSelected?.officeNm || '';

    const kanaName = staffInfo?.kanaFamily && staffInfo?.kanaGiven
      ? `${staffInfo.kanaFamily} ${staffInfo.kanaGiven}`
      : staffInfo?.kanaFamily || staffInfo?.kanaGiven || '';

    const fullName = staffInfo?.family && staffInfo?.given
      ? `${staffInfo.family} ${staffInfo.given}`
      : staffInfo?.family || staffInfo?.given || '';

    // TV thì lấy tên thiết bị thật, Web/Browser thì chỉ cần ghi 'Browser' (userAgent chi tiết đã có ở field userAgent riêng)
    const isTvMode = this.localStorageService.getItem(LOCAL_STORAGE_KEY.mode) == Mode.TV;
    const deviceName = isTvMode ? this.firebaseDeviceInfoService.getDeviceName() : 'Browser';
    const deviceNameLog = deviceName ? `deviceName: ${deviceName} |` : '';

    params.module = 'CAREMONI';
    params.ip = this.clientIp;
    params.userAgent = `${deviceNameLog} ${window.navigator.userAgent}`;
    params.userId = staffInfo?.stfCd || '';
    params.token = loginSession?.accessToken || '';
    params.email = params?.email || '';
    params.phoneNumber = params?.phoneNumber || '';
    params.userKana = kanaName;
    params.userName = fullName;
    params.accessDatetime = new Date().toISOString();
    params.action = params?.action || ACTION_LOG.ACTION_ERROR;
    params.screenName = params?.screenName || '';
    params.logType = params?.logType || LOG_TYPE.LOG_ERROR;
    params.apiPath = params?.apiPath || '';
    params.content = this.parseToString(params?.content) || '';
    params.body = this.parseToString(params?.body) || '';
    params.userType = 'user';
    params.statusCode = params?.statusCode || 200;
    params.errorMessage = this.parseToString(params?.errorMessage) || '';
    params.organizationId = organizationId || '';
    params.organizationName = companyName || '';
    params.companyId = companyId;
    params.companyName = officeName;
    params.method = params?.method || '';

    return params;
  }

  saveLogsToLocalStorage() {
    this.localStorageService.setSaucerLogs(this.logs);
  }

  runSaucerLog() {
    this.getClientIp().subscribe({
      next: (clientIp) => {
        this.setClientIp(clientIp);
      },
      complete: () => {
        const tempSaucerLogs = this.localStorageService.getSaucerLogs();
        if (tempSaucerLogs.length > 0) {
          let listBulk: any[] = [];
          const chunks = this.chunkArray(tempSaucerLogs, CONFIG_LOG.MAX_COUNT_TO_CALL_SAUCER_LOG);
          for (const chunk of chunks) {
            listBulk.push(this.sendLogs(chunk).subscribe());
          }
          Promise.all(listBulk).then(() => {
            this.localStorageService.clearSaucerLogs();
          });
        }
      }
    });
  }

  getClientIp() {
    if (this.clientIp && this.clientIp !== this.failedIpString) return of({ip:this.clientIp});
    return this.http.get<any>(CONFIG_LOG.API_GET_CLIENT_IP)
      .pipe(map(data => {
        return data;
      }),
      catchError(() => {
        return of({ip: this.failedIpString});
      }))
  }

  async setClientIp(data: any) {
    if (!this.clientIp || this.clientIp === this.failedIpString) {
      this.clientIp = data?.ip || this.failedIpString;
    }
  }

  resetTimeout() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
    this.timeoutId = setTimeout(() => {
      this.sendLogs(this.logs).subscribe();
      this.logs = [];
    }, CONFIG_LOG.TIMEOUT_CALL_SAUCER_LOG);
  }

  setTimeoutToSendLogs() {
    if (this.logs.length >= CONFIG_LOG.MAX_COUNT_TO_CALL_SAUCER_LOG) {
      this.sendLogs(this.logs).subscribe();
      this.logs = [];
    }
    this.resetTimeout();
  }

  sendLogImmediately() {
    if(this.logs.length > 0) {
      this.sendLogs(this.logs).subscribe();
      this.logs = [];
    }
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
  }

  sendLogs(lstBulk: any[]) {
    if (!lstBulk || lstBulk.length === 0) {
      return EMPTY.pipe();
    }
    if (!CONFIG_LOG.IS_DEBUG) {
      const headers = new HttpHeaders({
        'Content-Type': 'application/json'
      });
      return this.http.post<any>(API_ENDPOINT.saucerLog.logBulk, {logs: lstBulk}, {
        headers: headers,
        observe: 'response'
        })
        .pipe(map(data => {
          return data;
        }));
    } else {
      if (CONFIG_LOG.IS_SHOW_LOCAL_LOG) {
        console.log('==== POST_TO_API ====')
        console.log({POSTED_API: this.logs});
        console.log('=====================');
      }
      return EMPTY.pipe();
    }
  }

  // Utility function to split the array into chunks of max size
  chunkArray(array: [], chunkSize: number) {
    const chunks = [];
    for (let i = 0; i < array.length; i += chunkSize) {
        chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
  }

  parseToString(inp: any) {
    // Check if 'a' is null or undefined and return an empty string
    if (inp === null || inp === undefined) {
      return '';
    }

    if (typeof inp === 'object') {
      try {
        return JSON.stringify(inp);
      } catch (error) {
        console.error('Error stringifying object:', error);
        return '';
      }
    }

    return String(inp);
  }

}